import { useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import { NAV_ITEMS } from '@/routes/routes';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { HomeIcon, ServerIcon, SettingsIcon } from '@/components/icons';
import styles from './MobileNav.module.css';

const ICONS: Record<string, React.ReactNode> = {
  home: <HomeIcon />,
  server: <ServerIcon />,
  settings: <SettingsIcon />,
};

interface MobileNavProps {
  isOpen: boolean;
  onClose: () => void;
}

export function MobileNav({ isOpen, onClose }: MobileNavProps) {
  const focusTrapRef = useFocusTrap<HTMLDivElement>(isOpen);
  
  // Close on escape
  useEffect(() => {
    if (!isOpen) return;
    
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);
  
  // Prevent body scroll
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [isOpen]);
  
  if (!isOpen) return null;
  
  return createPortal(
    <div className={styles.overlay} onClick={onClose}>
      <div
        ref={focusTrapRef}
        className={styles.drawer}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
      >
        <div className={styles.header}>
          <span className={styles.logo}>WHSS</span>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Close navigation menu"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <nav className={styles.nav}>
          <ul className={styles.navList}>
            {NAV_ITEMS.map((item) => (
              <li key={item.path}>
                <NavLink
                  to={item.path}
                  className={({ isActive }) =>
                    clsx(styles.navLink, isActive && styles.active)
                  }
                  onClick={onClose}
                >
                  <span className={styles.navIcon}>{ICONS[item.icon]}</span>
                  <span className={styles.navLabel}>{item.label}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
      </div>
    </div>,
    document.body
  );
}
